import React from "react";
import { Link } from "wouter";
import { useListMerchants } from "@workspace/api-client-react";
import { useI18n } from "@/contexts/I18nContext";
import { AlertCircle, ArrowRight, CheckCircle2, MapPin, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ListingImage } from "@/components/listing-image";

export default function CompaniesPage() {
  const { t, isRTL } = useI18n();
  const { data: merchants, isLoading, isError } = useListMerchants({ limit: 100 });

  return (
    <div className="flex-1 bg-muted/10 pb-20">
      <div className="container mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="mb-10 max-w-3xl">
          <Badge className="mb-4 rounded-full bg-primary/10 px-4 py-1.5 text-sm text-primary border border-primary/20 shadow-sm">
            <Store className="mr-2 h-4 w-4" />
            {t("USDT-friendly companies")}
          </Badge>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight md:text-5xl text-[#062A60] dark:text-white" data-testid="text-companies-heading">
            {t("Companies & Merchants")}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t("Browse companies and merchants, then explore the products and services they list.")}
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20" data-testid="companies-loading">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-destructive/20 bg-card px-6 py-16 text-center shadow-sm">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <h2 className="mb-2 text-xl font-bold">{t("Unable to load companies")}</h2>
            <p className="text-muted-foreground">{t("Please try again in a moment.")}</p>
          </div>
        ) : merchants?.length ? (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="companies-grid">
            {merchants.map((merchant) => {
              const merchantHref = `/merchants/${merchant.slug ?? merchant.id}`;
              return (
                <Link
                  key={merchant.id}
                  href={merchantHref}
                  className="group block h-full"
                  data-testid={`card-company-${merchant.id}`}
                >
                  <Card className="h-full overflow-hidden rounded-2xl border-border/60 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl flex flex-col">
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                      <ListingImage
                        src={merchant.imageUrl}
                        alt={merchant.name}
                        imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      
                      <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                        {merchant.isFeatured && (
                          <Badge className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm shadow-sm font-semibold gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {t("Featured")}
                          </Badge>
                        )}
                      </div>

                      <div className="absolute bottom-3 right-3">
                        <Badge variant="secondary" className="souq-category-badge backdrop-blur-md font-medium shadow-sm">
                          {merchant.categoryName}
                        </Badge>
                      </div>
                      
                      {/* Floating Logo */}
                      <div className="absolute -bottom-6 left-5 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border-4 border-card bg-background text-2xl font-bold text-primary shadow-lg ring-1 ring-border/20 z-10">
                        {merchant.logoUrl ? (
                          <img src={merchant.logoUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          merchant.name.charAt(0)
                        )}
                      </div>
                    </div>
                    
                    <CardContent className="flex h-full flex-col p-5 pt-10">
                      <h2 className="mb-2 text-xl font-bold leading-tight group-hover:text-primary transition-colors line-clamp-1" data-testid={`text-company-name-${merchant.id}`}>
                        {merchant.name}
                      </h2>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 font-medium">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{merchant.area}</span>
                        <span>•</span>
                        <Store className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {merchant.listingCount ?? 0} {(merchant.listingCount ?? 0) === 1 ? t("listing") : t("listings")}
                        </span>
                      </div>
                      
                      {merchant.description && (
                        <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-muted-foreground flex-1">
                          {merchant.description}
                        </p>
                      )}

                      <div className="mt-auto flex items-center justify-between border-t border-border/50 pt-4">
                        <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                          {t("View merchant")}
                        </span>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all" asChild>
                          <span>
                            <ArrowRight className={`h-4 w-4 ${isRTL ? "rtl-flip" : ""}`} />
                          </span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border/60 bg-card/50 px-6 py-20 text-center">
            <Store className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <h2 className="mb-2 text-2xl font-bold">{t("No companies listed yet")}</h2>
            <p className="mb-8 text-muted-foreground max-w-md">{t("Check back soon for more USDT-friendly merchants.")}</p>
            <Button size="lg" className="rounded-full px-8 shadow-md" asChild>
              <Link href="/explore">{t("Explore listings")}</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
